import React, { useState, useEffect } from 'react';
import { Receipt, Download } from 'lucide-react';
import API from '../../api';
import generateSalarySlipPDF from '../../utils/pdfUtils';

const MySalarySlips = ({ user }) => {
  const [salarySlips, setSalarySlips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSalarySlips();
  }, []);

  const fetchSalarySlips = async () => {
    try {
      const endpoint = `/salary?employee_id=${user.id}`;
      const { data } = await API.get(endpoint);
      const normalizedSlips = (data.salary_slips || []).map((slip) => ({
        ...slip,
        basic_salary: Number(slip.basic_salary),
        allowances: Number(slip.allowances),
        deductions: Number(slip.deductions),
        net_salary: Number(slip.net_salary),
        overtime_hours: Number(slip.overtime_hours),
        days_worked: Number(slip.days_worked),
        total_days: Number(slip.total_days),
      }));
      setSalarySlips(normalizedSlips);
    } catch (error) {
      console.error('Error fetching salary slips:', error);
      alert(error.message || 'Failed to fetch salary slips');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (slip) => {
    generateSalarySlipPDF(slip, user);
  };

  const getMonthName = (month) => {
    return new Date(2023, month - 1).toLocaleDateString('en-US', { month: 'long' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
          <Receipt className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900">My Salary Slips</h2>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Basic</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Allowances</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deductions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Net</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {salarySlips.map((slip) => (
                <tr key={slip.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">{getMonthName(slip.month)} {slip.year}</td>
                  <td className="px-6 py-4">₹{slip.basic_salary.toFixed(2)}</td>
                  <td className="px-6 py-4">+₹{slip.allowances.toFixed(2)}</td>
                  <td className="px-6 py-4">-₹{slip.deductions.toFixed(2)}</td>
                  <td className="px-6 py-4 font-semibold text-green-700">₹{slip.net_salary.toFixed(2)}</td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleDownload(slip)}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                    >
                      <Download className="w-4 h-4" /> <span>Download</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {salarySlips.length === 0 && (
          <div className="text-center py-12">
            <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No salary slips available yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MySalarySlips;
